/*
 * Accounts and school orders.
 * - Create-an-account form (sections/mm-account.liquid). Shopify's spam check (hCaptcha) watches its own
 *   contact and customer forms, so both are sent the normal way, one page after the other:
 *   1. the contact form, so the details reach the store inbox;
 *   2. only if they ticked "email me": a hidden customer form, which adds them to the customer list with
 *      tags (Shopify subscribes everyone sent through that form, so it's never used without the tick);
 *   3. Shopify's sign-in, with their email filled in. Confirming the 6-digit code creates the account,
 *      and Shopify brings them back to this page, signed in.
 *   Between pages the next step waits in sessionStorage.
 * - "Ordering for a school?" fields (snippets/mm-school-order.liquid): saved to the cart as attributes.
 */
(() => {
  'use strict';

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms))]);

  // ---------- Create-an-account form
  const NEXT_KEY = 'mm.accountNext';
  const nextStep = {
    read(maxAgeMs) {
      try {
        const next = JSON.parse(sessionStorage.getItem(NEXT_KEY));
        return next && Date.now() - next.at < maxAgeMs ? next : null;
      } catch (e) { return null; }
    },
    write(next) {
      try { sessionStorage.setItem(NEXT_KEY, JSON.stringify({ ...next, at: Date.now() })); return true; } catch (e) { return false; }
    },
    clear() {
      try { sessionStorage.removeItem(NEXT_KEY); } catch (e) { /* storage blocked: nothing saved */ }
    },
  };

  // Send a form once Shopify's spam check has had the chance to attach itself (it loads after the page).
  const submitWhenReady = (target) => {
    const go = () => setTimeout(() => target.requestSubmit(), 400);
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true });
  };

  if (document.querySelector('[data-mm-signed-in]')) nextStep.clear();

  const form = document.getElementById('MMAccountForm');
  const listForm = document.getElementById('MMAccountList');
  if (form && listForm) {
    const field = (name) => form.elements[name];
    const schoolLabel = form.querySelector('[data-mm-school-label]');
    const params = new URLSearchParams(location.search);

    const setType = (type) => {
      const school = type !== 'family';
      form.dataset.type = school ? 'school' : 'family';
      schoolLabel.textContent = school ? 'School name' : "Student's school";
      field('contact[School]').placeholder = school ? 'School name' : 'Where the student goes to school';
      field('contact[name]').placeholder = school ? 'Ms Aroha Smith' : 'Your name';
      field('contact[email]').placeholder = school ? 'you@school.nz' : 'you@example.com';
    };

    form.querySelectorAll('[data-mm-acct-type]').forEach((radio) => {
      radio.addEventListener('change', () => radio.checked && setType(radio.dataset.mmAcctType));
    });
    if (params.get('type') === 'family') form.querySelector('[data-mm-acct-type="family"]').checked = true;
    setType(form.querySelector('[data-mm-acct-type]:checked').dataset.mmAcctType);

    // The customer form wants first and last names; tags let the store filter school accounts.
    const listData = (email) => {
      const names = field('contact[name]').value.trim().split(/\s+/);
      const school = field('contact[School]').value.replace(/,/g, ' ').trim();
      const tags = ['Account form', form.dataset.type === 'school' ? 'School account' : 'Family account'];
      if (school) tags.push('School: ' + school);
      return {
        'contact[email]': email,
        'contact[first_name]': names.shift() || '',
        'contact[last_name]': names.join(' '),
        'contact[tags]': tags.join(','),
      };
    };

    const listStep = params.get('step') === 'list' && nextStep.read(10 * 60 * 1000);
    const signInStep = nextStep.read(2 * 60 * 1000);
    if (listStep && listStep.step === 'list') {
      // Step 2: add them to the customer list; Shopify then sends them on to sign-in.
      nextStep.write({ step: 'signin', signIn: listStep.signIn });
      form.closest('[data-mm-acct-box]').classList.add('is-busy');
      Object.entries(listStep.list).forEach(([name, value]) => { listForm.elements[name].value = value; });
      listForm.elements.return_to.value = listStep.signIn;
      submitWhenReady(listForm);
    } else if (signInStep && signInStep.step === 'signin') {
      // Step 2 came back here instead of going on to sign-in (e.g. the list refused the email).
      nextStep.clear();
      location.replace(signInStep.signIn);
    }

    form.addEventListener('submit', () => {
      // Shopify's spam check may hold this submit and send it itself, so only set values here.
      const email = field('contact[email]').value.trim();
      const signIn = '/customer_authentication/login?login_hint=' + encodeURIComponent(email) +
        '&return_to=' + encodeURIComponent(form.dataset.welcomeUrl || location.pathname + '?welcome=1');
      let returnTo = signIn;
      if (field('contact[Email updates]').checked && nextStep.write({ step: 'list', signIn, list: listData(email) })) {
        returnTo = location.pathname + '?step=list';
      }
      field('return_to').value = returnTo;
      form.querySelector('[type="submit"]').textContent = 'Creating your account…';
    });
  }

  // ---------- "Ordering for a school?" fields
  let timer = null;
  let saved = Promise.resolve();

  const saveSchoolFields = () => {
    clearTimeout(timer);
    timer = null;
    const attributes = {};
    document.querySelectorAll('[data-mm-attr]').forEach((input) => {
      attributes[input.dataset.mmAttr] = input.value.trim();
    });
    saved = saved
      .then(() => fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      }))
      .catch(() => {});
    return saved;
  };

  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-mm-attr]')) return;
    clearTimeout(timer);
    timer = setTimeout(saveSchoolFields, 600);
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-mm-attr]')) saveSchoolFields();
  });
  // Enter in these fields would submit the cart form, which on the cart page means checkout.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches('[data-mm-attr]')) return;
    event.preventDefault();
    saveSchoolFields();
  });

  // The drawer's checkout is a plain link, so finish saving before leaving the page.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-mm-checkout]');
    if (!link || event.defaultPrevented || link.getAttribute('aria-disabled') === 'true') return;
    if (!document.querySelector('[data-mm-attr]')) return;
    event.preventDefault();
    if (timer) saveSchoolFields();
    withTimeout(saved, 3000).catch(() => {}).then(() => { location.href = link.href; });
  });
})();
